import { useAppSelector } from '@common/reducers/hooks/store.hook';
import { selectIsChatActive, selectIsCurrentChatReadOnly } from './redux/TeamInboxSelectors';
import { getRuntimeUrl } from '@common/services/storage';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Box, Paper, Typography, alpha, useTheme } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import ChatHeader from './chat-header';
import MessageInputBar from './message-input-bar';
import classes from './team-inbox.module.scss';
import WaMessageLazyLoadingVirtuoso from './wa-message-lazy-loading-virtuoso';

const chatWelcomeImageSrc = getRuntimeUrl("/img/chat-welcome.svg");

const ChatView = React.memo(() => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isChatActive = useAppSelector(selectIsChatActive);
    const isCurrentChatReadOnly = useAppSelector(selectIsCurrentChatReadOnly);
    const [isDragging, setIsDragging] = useState(false);
    const [droppedFiles, setDroppedFiles] = useState<File[]>([]);



    // Handle clearing dropped files
    const handleClearDroppedFiles = useCallback(() => {
        setDroppedFiles([]);
    }, []);

    // Setup dropzone for the entire chat view
    const { getRootProps, getInputProps } = useDropzone({
        onDrop: (acceptedFiles) => {
            if (isChatActive && !isCurrentChatReadOnly) {
                setDroppedFiles(acceptedFiles);
            }
        },
        onDragEnter: () => setIsDragging(true),
        onDragLeave: () => setIsDragging(false),
        onDropAccepted: () => setIsDragging(false),
        onDropRejected: () => setIsDragging(false),
        noClick: true,
        noDragEventsBubbling: true,
        disabled: !isChatActive || isCurrentChatReadOnly
    });

    return (
        <Box
            {...getRootProps()}
            className={classes.chatView}
            sx={{
                backgroundColor: 'var(--wa-chat-bg)',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                position: 'relative',
            }}
        >
            <input {...getInputProps()} />

            {/* Drag overlay - shown only when dragging files */}
            {isDragging && isChatActive && !isCurrentChatReadOnly && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: 1000,
                        backgroundColor: alpha(theme.palette.background.paper, 0.9),
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        pointerEvents: 'none',
                        border: `3px dashed ${theme.palette.primary.main}`,
                        borderRadius: '8px',
                    }}
                >
                    <AttachFileIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }} />
                    <Typography variant="h6" color="primary.main" fontWeight={600}>
                        {t('teamInbox.dropFileToSend')}
                    </Typography>
                </Box>
            )}

            <ChatHeader />

            {/* Chat View Component */}
            {isChatActive && (
                <>
                    <Box sx={{
                        flexGrow: 1,
                        overflow: 'hidden',
                        position: 'relative',
                        backgroundColor: 'transparent'
                    }}>
                        <WaMessageLazyLoadingVirtuoso />
                    </Box>
                    {!isCurrentChatReadOnly && (
                        <MessageInputBar
                            droppedFiles={droppedFiles}
                            onClearDroppedFiles={handleClearDroppedFiles}
                        />
                    )}
                    {isCurrentChatReadOnly && (
                        <Paper
                            elevation={0}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '16px',
                                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                                backdropFilter: 'blur(10px)',
                                borderTop: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
                            }}
                        >
                            <Box sx={{
                                color: theme.palette.text.secondary,
                                fontSize: '0.95rem',
                                fontWeight: 400,
                                textAlign: 'center',
                            }}>
                                {t('teamInbox.onlyAdminsCanSendMessages')}
                            </Box>
                        </Paper>
                    )}
                </>
            )}

            {/* Welcome Screen Component */}
            {!isChatActive && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        backgroundColor: 'var(--wa-panel-bg)',
                        p: 4,
                        textAlign: 'center',
                    }}
                >
                    <img
                        src={chatWelcomeImageSrc}
                        alt={t('teamInbox.welcomeImageAlt', 'Welcome')}
                        style={{
                            width: '240px',
                            height: '240px',
                            opacity: 0.9,
                            marginBottom: '16px'
                        }}
                    />
                    <Typography variant="h5" fontWeight={600} sx={{ color: 'var(--wa-secondary-text)' }} gutterBottom>
                        {t('teamInbox.welcomeTitle')}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'var(--wa-secondary-text)', maxWidth: '500px' }}>
                        {t('teamInbox.welcomeMessage')}
                    </Typography>
                </Box>
            )}
        </Box>
    );
});

export default ChatView;